import { useRef } from 'react';
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Info } from 'lucide-react';
import Chip from '../common/Chip';
import { Tooltip } from '../ui/tooltip';
import { List, ListItem } from '../ui/list';

interface AttachmentsProps {
  attachments: File[] | undefined;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  fileError?: string;
}

const Attachments = ({
  attachments,
  onFileChange,
  onRemoveFile,
  fileError,
}: AttachmentsProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const removeFile = (index: number) => {
    const newFiles = (attachments || []).filter((_, i) => i !== index);
    onRemoveFile(index);

    if (newFiles.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Attachments</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="attachments">Upload Screenshots or Documents</Label>
            <Tooltip
              content={
                <div className="w-58 py-1">
                  <h3 className="font-medium text-sm mb-2 text-gray-900">
                    Attachment Requirements:
                  </h3>
                  <List className="text-xs space-y-2 text-gray-700">
                    <ListItem className="flex items-start gap-1">
                      <span className="w-1.5 h-1.5 bg-red-800 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>formats: PDF,DOC,DOCX,TXT</span>
                    </ListItem>
                    <ListItem className="flex items-start gap-1">
                      <span className="w-1.5 h-1.5 bg-red-800 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>Image formats: JPG,PNG</span>
                    </ListItem>
                    <ListItem className="flex items-start gap-1">
                      <span className="w-1.5 h-1.5 bg-red-800 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>Maximum file size: 5MB per file</span>
                    </ListItem>
                    <ListItem className="flex items-start gap-1">
                      <span className="w-1.5 h-1.5 bg-red-800 rounded-full mt-1.5 flex-shrink-0"></span>
                      <span>Maximum 2 files allowed</span>
                    </ListItem>
                  </List>
                </div>
              }
            >
              <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
            </Tooltip>
          </div>
          <Input
            id="attachments"
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.txt"
            onChange={onFileChange}
            className="cursor-pointer"
            ref={fileInputRef}
          />
          {fileError && <p className="text-sm text-red-500">{fileError}</p>}
        </div>

        {attachments && attachments.length > 0 && (
          <div className="space-y-2">
            <Label>Attached Files</Label>
            <div className="flex gap-2 flex-wrap">
              {attachments.map((file, index) => (
                <Chip
                  key={index}
                  fileName={file.name}
                  fileSize={formatFileSize(file.size)}
                  onRemove={() => removeFile(index)}
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Attachments;
